import parse from "html-react-parser"
import { useTranslation } from "react-i18next"

import { CourseList } from "@/features/training/components/course-list"
import { DemoFlowPreview } from "@/features/training/components/demo"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"

type Exercise = {
  title: string
  steps: string[]
}

export function FlowsGettingStartedPage() {
  const { t } = useTranslation("", { keyPrefix: "training.flows.getting-started" })
  const list = <T = string,>(key: string) => ensureArray<T>(t(key, { returnObjects: true }))

  return (
    <CourseLayout>
      <CourseSection>
        <h1>{t("title")}</h1>
        <h2>{t("purpose.title")}</h2>
        <p>{parse(t("purpose.description"))}</p>
        <p>
          <strong>{t("purpose.level")}</strong>
        </p>
      </CourseSection>

      <CourseSection>
        <h2>{t("learning_objectives.title")}</h2>
        <p>{t("learning_objectives.description")}</p>
        <CourseList data={list("learning_objectives.objectives")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("what_is.title")}</h2>
        <p>{parse(t("what_is.description"))}</p>
        <p>
          <strong>{t("what_is.core_parts_label")}</strong>
        </p>
        <CourseList data={list("what_is.core_parts")} />
        <div className="my-6">
          <DemoFlowPreview />
        </div>
      </CourseSection>

      <CourseSection>
        <h2>{t("when_to_use.title")}</h2>
        <p>{parse(t("when_to_use.description"))}</p>
        <CourseList data={list("when_to_use.signs")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("build_steps.title")}</h2>
        <p>{parse(t("build_steps.intro"))}</p>
        <CourseList data={list("build_steps.steps")} />
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(t("build_steps.tip"))}</p>
        </blockquote>
      </CourseSection>

      <CourseSection>
        <h2>{t("test_and_run.title")}</h2>
        <p>{parse(t("test_and_run.intro"))}</p>
        <CourseList data={list("test_and_run.options")} />
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(t("test_and_run.remember"))}</p>
        </blockquote>
      </CourseSection>

      <CourseSection>
        <h2>{t("exercises.title")}</h2>
        {list<Exercise>("exercises.items").map((exercise, index) => (
          <div key={index}>
            <h3>{exercise.title}</h3>
            <CourseList data={ensureArray<string>(exercise.steps)} />
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("pitfalls.title")}</h2>
        <CourseList data={list("pitfalls.tips")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("summary.title")}</h2>
        <CourseList data={list("summary.points")} />
      </CourseSection>
    </CourseLayout>
  )
}
